import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Pagination from "react-bootstrap/Pagination";
import PieChart from "@core/compare-trends-charts/PieChartWithLegends";
import commaFormat from "@utils/commaFormat";
import { getIntelUrl } from "@utils/intelUrl";
import { getTitleCase } from "@utils/utils";
import ReactCountryFlag from "react-country-flag";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import EmptyStateTradeOverview from "./EmptyStateTradeOverview";

const Country = ({ activeCategData, companyName, pathname, dataSource, userActivePlan, profileData, activeCategory, activePanelId, expandedItems, headings, toggleExpand, lastPage, loader, fetchTradeOverview }) => {
  const countDetails = useSelector((state) => state.companyProfile.countDetails);
  const {
    userData: { industry: profileType, intel_sub_plan: intelSubPlan },
  } = useSelector((state) => state.userDetails);
  const isLogisticsPlan = intelSubPlan === "logistics";
  const isLogisticsProfile = profileType === "logistics provider";

  const category_count = countDetails[activeCategData?.countEnum];

  // these are used for pagination
  const [currentPage, setCurrentPage] = useState(lastPage);
  const itemsPerPage = 10;
  const [paginatedData, setPaginatedData] = useState([]);
  const totalPages = Math.ceil(category_count / itemsPerPage);

  useEffect(() => {
    if (profileData[activeCategory]) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPaginatedData(profileData[activeCategory].slice(startIndex, endIndex));
    }
  }, [currentPage, profileData, activeCategory]);

  const handlePageChange = async (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationForwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));
      if (currentPage * itemsPerPage >= profileData[activeCategory].length) {
        await fetchTradeOverview(currentPage);
      }
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationBackwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));
      setCurrentPage(currentPage - 1);
    }
  };

  const renderPagination = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, category_count);
    const showPagination = !(currentPage * itemsPerPage >= category_count && currentPage === 1);

    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
        <span>
          Showing {startItem}-{endItem} of
        </span>
        <span className="tw-ml-1">
          <Pagination>
            <Pagination.Item className="page-item pagination-item">{category_count}</Pagination.Item>
            {showPagination && (
              <>
                <Pagination.Prev disabled={currentPage === 1} className="page-item pagination-prev" onClick={() => handlePageChange("prev")} />
                <Pagination.Next disabled={currentPage * itemsPerPage >= category_count} className="page-item pagination-next" onClick={() => handlePageChange("next")} />
              </>
            )}
          </Pagination>
        </span>
      </div>
    );
  };

  const activeHeading = headings.find((heading) => heading.id === activeCategData?.id && activePanelId === heading.activePanelId);

  const linkGenerator = (data) => {
    let link = "";
    const searchUrlParams = window.location.search;
    const country = new URLSearchParams(searchUrlParams).get("country");
    const type = new URLSearchParams(searchUrlParams).get("type");
    const redirectionType = activeHeading.redirectionType;
    const redirectprofileType = activeHeading.redirectprofileType;
    const slug = data.slug;
    const entityId = data.port_id;

    link = getIntelUrl() + `${redirectionType}/` + `${slug}/` + `${entityId}?` + `country=${country}` + "&" + `type=${type}` + "&" + `profileName=${redirectprofileType}`;

    return link;
  };
  const handleCardToggle = (itemKey) => {
    toggleExpand(itemKey);
  };

  return loader ? (
    <LoadingSkeleton lines={6} lineHeight="35px" width="100%" shape="rectangle" />
  ) : paginatedData?.length > 0 ? (
    <>
      {<span className="tw-font-medium tw-text-gray-900">{`${activePanelId === "importers" && activeCategory === "trade_country" ? "Countries " + companyName + " Shipped from" : activePanelId === "exporters" && activeCategory === "trade_country" ? "Countries " + companyName + " Shipped to" : ""}`}</span>}
      {paginatedData?.map((item, iindex) => {
        const itemKey = `${item.s_no}-${activePanelId}-${activeCategData.id}`;
        const isExpanded = expandedItems.includes(itemKey);
        return (
          item.key && (
            <div key={item.s_no} className={`tw-flex tw-flex-col tw-font-semibold tw-gap-2 tw-cursor-pointer ${!isExpanded ? "tw-overflow-hidden" : "tw-bg-gray-100"} tw-border tw-rounded-lg tw-px-4 tw-py-3`} onClick={() => handleCardToggle(itemKey)}>
              <div className={`tw-flex tw-items-center ${activeCategData?.isSecondDiv === true ? "tw-justify-end" : "tw-justify-between"}`}>
                {activeCategData?.keyTop && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <ReactCountryFlag className="tw-w-4 tw-h-3" countryCode={item.countryCode} svg />
                    <span className="tw-font-semibold tw-text-gray-900">{item[activeCategData.keyTop].includes("russia") ? getTitleCase(item[activeCategData.keyTop]) + " (Mirror)" : getTitleCase(item[activeCategData.keyTop])}</span>
                  </div>
                )}
                {activeCategData?.isExpandable && (
                  <div
                    className="tw-cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(itemKey);
                    }}>
                    {(isLogisticsProfile ? item[activeCategData.key2Alt]?.length >= 1 || item[activeCategData.key1]?.length >= 1 : item[activeCategData.key2]?.length > 1 || item[activeCategData.key1]?.length > 1) && <span className={`material-symbols-outlined tw-w-5 tw-h-5 tw-border tw-bg-white tw-border-gray-300 tw-rounded-[4px] tw-flex tw-items-center tw-justify-center`}>{isExpanded ? "collapse_content" : "expand_content"}</span>}
                  </div>
                )}
              </div>
              {activeHeading && (
                <>
                  <div className="tw-flex">
                    <div className={`tw-flex tw-flex-col tw-gap-1 ${isLogisticsProfile ? "tw-w-[25%]" : "tw-w-[25%]"}`}>
                      <span className="tw-font-normal tw-text-gray-700">{activeHeading.heading1}</span>
                      <span className="tw-font-medium tw-text-gray-900">{typeof item[activeCategData?.key] === "string" ? getTitleCase(item[activeCategData?.key]) : commaFormat(item[activeCategData?.key])}</span>
                    </div>
                    {activeHeading.heading2 && (
                      <div className={`tw-flex tw-flex-col tw-gap-1 ${isLogisticsProfile ? "tw-w-[40%]" : "tw-w-[43%]"}`}>
                        {item[activeCategData?.key1] ? (
                          <span className="tw-font-normal tw-text-gray-700">
                            {activeHeading.heading2} {activeCategory === "trade_country" && getTitleCase(item.key)}
                          </span>
                        ) : (
                          "NA"
                        )}
                        <div className={`tw-flex tw-gap-1 tw-font-medium ${isLogisticsProfile ? "tw-w-[85%]" : "tw-w-[350px]"} ${isExpanded ? "tw-flex-col" : ""}`}>
                          {Array.isArray(item[activeCategData?.key1]) && item[activeCategData?.key1].length > 0
                            ? item[activeCategData?.key1]?.map((subItem, index) => (
                                <div className="tw-flex tw-items-center" key={index}>
                                  <span className="tw-flex tw-flex-col tw-gap-1 tw-text-gray-900" data-tip={getTitleCase(subItem[activeCategData?.key1item])} data-for={`xyz-${item.s_no}-${index}`}>
                                    {(index === 0 || isExpanded) && (subItem[activeCategData?.key1item]?.length > 35 ? getTitleCase(subItem[activeCategData?.key1item]) : getTitleCase(subItem[activeCategData?.key1item]))}
                                    {index !== item[activeCategData?.key1].length - 1 && isExpanded ? ", " : ""}
                                  </span>
                                  {index === 0 && !isExpanded && item[activeCategData?.key1].length > 1 && (
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(itemKey);
                                      }}
                                      className="tw-flex tw-items-center tw-ml-1 tw-py-[0.1rem] tw-px-[0.3rem] tw-cursor-pointer tw-bg-white tw-border tw-border-gray-200 tw-text-gray-900 tw-text-xs tw-shadow tw-rounded-full">
                                      +{item[activeCategData?.key1].length - 1}
                                    </span>
                                  )}
                                </div>
                              ))
                            : "NA"}
                        </div>
                      </div>
                    )}

                    <div className={`tw-flex tw-gap-1 tw-flex-col ${isExpanded ? "" : ""} ${isLogisticsProfile ? "tw-w-[35%]" : "tw-w-[32%]"}`}>
                      <span className={`tw-font-normal tw-text-gray-700 ${isExpanded ? "tw-flex" : ""} ${isLogisticsProfile ? "tw-flex" : ""}`}>{isLogisticsProfile ? activeHeading.heading3Alt : activeHeading.heading3}</span>
                      <div className={`tw-flex tw-gap-1 tw-font-medium tw-text-gray-900 ${isExpanded ? "" : "tw-w-[200px]"}  ${isLogisticsProfile ? "" : ""}`}>
                        {Array.isArray(item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2]) && item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].length > 0
                          ? item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].map((subItem, index) => (
                              <div className="tw-flex tw-items-center" key={index}>
                                <span className="tw-flex tw-flex-col tw-gap-1">
                                  <span>{index === 0 && !isExpanded && getTitleCase(subItem[isLogisticsProfile ? activeCategData?.key2itemAlt : activeCategData?.key2item])}</span>
                                  {isExpanded && (
                                    <PieChart
                                      isLogisticsProfile={isLogisticsProfile && isLogisticsPlan}
                                      pathname={pathname}
                                      dataSource={dataSource}
                                      userActivePlan={userActivePlan}
                                      chartKey={`PieChart${iindex}`}
                                      chartData={item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].map((item) => ({
                                        label: getTitleCase(item[isLogisticsProfile ? activeCategData?.key2itemAlt : activeCategData?.key2item]),
                                        value: item[activeCategData?.key],
                                      }))}
                                      linkForRedirection={item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].map((itemm) => linkGenerator(itemm))}
                                    />
                                  )}
                                </span>
                                {index === 0 && !isExpanded && item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].length > 1 && (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpand(itemKey);
                                    }}
                                    className="tw-flex tw-items-center tw-justify-center tw-ml-1 tw-bg-white tw-py-[0.1rem] tw-px-[0.3rem] tw-cursor-pointer tw-border tw-border-gray-200 tw-text-gray-900 tw-text-xs tw-shadow tw-rounded-full">
                                    +{item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].length - 1}
                                  </span>
                                )}
                              </div>
                            ))
                          : "NA"}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        );
      })}
      {category_count > 3 && renderPagination()}
    </>
  ) : (
    <EmptyStateTradeOverview description="No Countries Found" />
  );
};

export default Country;
